import { isClient } from '@vueuse/core'

// 客户端中间件：未登录用户，累计可见时间超过阈值后弹登录提示；每日只弹一次（弹出即计入
export default defineNuxtRouteMiddleware((to, from) => {
  // 只在浏览器端运行
  if (!isClient) return

  // 任何路由切换都先清理上一个页面可能留下的 handler / timer，
  // 避免离开 loginDialog 页面后弹窗仍在新页面触发
  try {
    const win = window as any
    if (win.__nuxt_report_cleanup__) {
      try {
        win.__nuxt_report_cleanup__()
      } catch (e) {
        /* ignore */
      }
      win.__nuxt_report_cleanup__ = null
    }
  } catch (e) {
    // ignore
  }

  // 如果路由没有开启 loginDialog 开关，直接返回
  if (!to.meta.loginDialog) return

  // 获取系统 store
  const systemStore = useMySystemStore()

  // 如果已登录，直接返回
  if (systemStore?.userIsLogin) return

  // 每日只弹一次：localStorage key 存储最近弹窗日期（格式 YYYY-MM-DD）  localStorage key 与今日字符串
  const LAST_SHOWN_KEY = '__report_last_shown_date__'
  const todayStr = new Date().toISOString().slice(0, 10) // YYYY-MM-DD

  try {
    const lastShown = localStorage.getItem(LAST_SHOWN_KEY)
    if (lastShown === todayStr) {
      // 今日已弹出过，直接返回
      return
    }
  } catch (err) {
    // localStorage 如果不可用则跳过每日限制（不常见）
  }

  // --- 计时逻辑：只累计页面可见时间（避免切后台也计时） ---
  let accumulated = 0 // 已累计的可见时间（ms）
  let activeStart = document.visibilityState === 'visible' ? Date.now() : 0 // 开始计时的时间戳
  const THRESHOLD = isDev() ? 2_00 : 20_000 // 20s（ms）

  // 检查函数（周期性检查）
  const interval = setInterval(() => {
    const now = Date.now()
    const currentAccum = accumulated + (activeStart ? now - activeStart : 0)
    if (currentAccum >= THRESHOLD) {
      clearAndShow()
    }
  }, 500) // 每 500ms 检查一次（你也可以改为 1000ms）

  // visibility change 处理：切换可见/不可见时累加/暂停
  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      // 恢复计时
      if (!activeStart) activeStart = Date.now()
    } else {
      // 暂停计时，累加已计时段
      if (activeStart) {
        accumulated += Date.now() - activeStart
        activeStart = 0
      }
    }
  }
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // beforeunload handler（注册时使用 named function 以便移除）
  function onBeforeUnload() {
    cleanup()
  }
  window.addEventListener('beforeunload', onBeforeUnload, { once: true })

  // 路由离开或下一次中间件运行时要清理
  function cleanup() {
    try {
      clearInterval(interval)
    } catch (e) {}
    try {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    } catch (e) {}
    try {
      window.removeEventListener('beforeunload', onBeforeUnload)
    } catch (e) {}
  }

  // 将清理函数暴露到 window 以便下次中间件执行时能清理掉
  ;(window as any).__nuxt_report_cleanup__ = cleanup

  // 最终触发弹窗的逻辑（在触发前再校验是否已登录 / 今日是否弹出）
  async function clearAndShow() {
    // 先清理定时器与监听
    cleanup()
    // 及时清除暴露引用，避免重复调用
    ;(window as any).__nuxt_report_cleanup__ = null
    try {
      // 再次判断登录状态（用户可能在期间登录了）
      if (systemStore?.userIsLogin) return

      // 再次检查每日限制 （避免 race）（并处理 localStorage 读取异常）
      try {
        const lastShown2 = localStorage.getItem(LAST_SHOWN_KEY)
        if (lastShown2 === todayStr) return
      } catch (e) {
        // ignore
      }

      // 文案（中英文）
      const isCn = systemStore?.language === 'zh-CN'
      const message = isCn
        ? '登录后，可查看更多企业的风险详情！'
        : 'Sign in to view more risk details!'
      const metaMessage = (
        isCn ? to.meta.loginDesCn : to.meta.loginDesEn
      ) as string
      const confirmText = isCn ? '去登录' : 'Sign In'
      const cancelText = isCn ? '取消' : 'Cancel'

      let confirmed = false
      const win = window as any

      // 优先使用项目自定义的 showConfirmDialog（支持自定义按钮文本并返回 Promise<boolean>）
      // 你项目中若有该方法，请确保接口类似 { title, message, confirmButtonText, cancelButtonText }
      if (typeof showConfirmDialog === 'function') {
        showConfirmDialog({
          // title: ''
          message: metaMessage ?? message,
          confirmButtonText: confirmText,
          cancelButtonText: cancelText,
        })
          .then(async () => {
            confirmed = true
            // 跳转到登录页
            go2LoginPageWithCrmSource(
              to && to.fullPath,
              (systemStore?.isAPP
                ? to.meta?.appClickSource
                : to.meta?.clickSource) as string,
            )
          })
          .catch(() => {})
      } else {
        console.error('⚠️———— showConfirmDialog is not a function')
      }

      // 标记今日已弹出（不论用户是否确认）
      try {
        localStorage.setItem(LAST_SHOWN_KEY, todayStr)
      } catch (e) {
        // ignore
      }
    } catch (err) {
      // 忽略错误，避免影响主流程
      // console.error('report middleware error', err)
    }
  }

  // 如果页面当前可见，激活计时；否则等 visibilitychange 触发
  if (!activeStart && document.visibilityState === 'visible') {
    activeStart = Date.now()
  }
})
